import { IWixAPI } from '@wix/yoshi-flow-editor';
import { SECTION_CATEGORY } from '@wix/communities-blog-client-common';
import { pageOpened } from '../../../common/actions/page-opened';
import { isPostContentRequired } from '../../../common/selectors/layout-selectors';
import {
  getIsValidPage,
  getLazyPaginationParams,
  getShowPagination,
} from '../../../common/selectors/pagination-selectors';
import { getPostsByHashtag } from '../../../common/selectors/post-selectors';
import { HASHTAG_PAGE } from '../../../common/services/detect-route';
import { generateHashtagSEOTags } from '../../../common/services/generate-seo-tags/generate-hashtag-seo-tags';
import { setMetaTagRobotsNoIndex } from '../../../common/services/set-metatag-robots-noindex';
import {
  getBasicParams,
  isSSR,
  isSite,
} from '../../../common/store/basic-params/basic-params-selectors';
import { fetchProfileUrls } from '../../../common/store/profile-urls/profile-urls-actions';
import { AppStore, RouteResolverFn } from '../../../common/types';
import { fetchHashtagFeedRenderModel } from '../../actions/fetch-feed-render-model';
import { ROUTE_404, RouteHashtagParams } from '../../constants/routes';

export const createHashtagPageRouter = (
  store: AppStore,
  wixCodeApi: IWixAPI,
): RouteResolverFn<RouteHashtagParams> => {
  return async ({ params }, redirect, { preFetch, preFetchResult }) => {
    const page = parseInt(params.pageIndex || '1', 10);
    const hashtag = params.hashtag;
    const state = store.getState();
    const feedPosts = getPostsByHashtag(state, hashtag);
    const basicParams = getBasicParams(state);

    const actions = preFetchResult || [
      store.dispatch(
        fetchHashtagFeedRenderModel({
          page,
          hashtagOptions: { hashtag },
          pageSize: getLazyPaginationParams({
            state,
            section: SECTION_CATEGORY,
            page,
          })?.size,
          includeContent: isPostContentRequired(state, SECTION_CATEGORY),
          includeInitialPageData: feedPosts.length === 0,
          language: basicParams.language,
        }),
      ),
    ];

    if (preFetch) {
      return Promise.all(actions);
    }

    if (preFetchResult) {
      await preFetchResult;
      store.dispatch(fetchProfileUrls());
    } else {
      await Promise.all(actions);
    }

    if (isInvalidPage(hashtag)) {
      return redirect(ROUTE_404);
    }

    addSeoTags(page, hashtag);

    trackPageOpen();
  };

  function isInvalidPage(hashtag: string) {
    const state = store.getState();
    const hasNoPosts = getPostsByHashtag(state, hashtag).length === 0;
    const showPagination = getShowPagination(state, SECTION_CATEGORY);
    const isValidPage = getIsValidPage(state, SECTION_CATEGORY);

    return (showPagination && !isValidPage) || hasNoPosts;
  }

  function addSeoTags(page: number, hashtag: string) {
    const state = store.getState();

    if (!isSite(state)) {
      setMetaTagRobotsNoIndex(wixCodeApi);

      return;
    }

    const posts = getPostsByHashtag(state, hashtag);

    const hashtagSEOTags = generateHashtagSEOTags({
      state,
      hashtag,
      page,
      posts,
      multilingual: wixCodeApi.window.multilingual,
    });
    wixCodeApi.seo.renderSEOTags(hashtagSEOTags);
  }

  function trackPageOpen() {
    const state = store.getState();

    if (isSSR(state)) {
      return;
    }

    store.dispatch(pageOpened({ page: HASHTAG_PAGE }));
  }
};
